import React, { useEffect, useState, useRef } from 'react';
import {
  IconButton, Badge, Menu, ListItemText, Typography, Divider, Box, MenuItem, Button
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './NotificationBell.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const stripCountPrefix = (title) => title.replace(/^(\(\d+\)\s*)+/, '');

const NotificationBell = () => {
  // store base title only once (initializer runs once)
  const [baseTitle] = useState(() => stripCountPrefix(document.title));
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("authToken");

  const open = Boolean(anchorEl);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  useEffect(() => {
    fetchNotifications();
  }, []);
  // update document.title whenever unreadCount changes
  useEffect(() => {
    const newTitle = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
    if (document.title !== newTitle) {
      document.title = newTitle;
    }
    // optional: restore clean base title on unmount
    return () => {
      document.title = baseTitle;
    };
  }, [unreadCount, baseTitle]);
  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/notifications`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error('Erreur de récupération des notifications :', error.message);
    }
  };

  const markMissionAsRead = async (userId, missionId, token) => {
    if (!userId || !missionId || !token) return;
    try {
      await axios.post(
        `${API_BASE}/api/recruteur/mark-read/${userId}`,
        { missionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erreur lors du marquage de la mission comme lue :', error);
    }
  };

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const markAsReadAndRedirect = async (notif) => {
    try {
      await axios.put(`${API_BASE}/api/notifications/${notif._id}/read`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (notif.missionId) {
        await markMissionAsRead(userId, notif.missionId, authToken);
      }

      fetchNotifications();
      handleClose();

      if (notif.missionId) {
        navigate(`/Recruteur/Details/${notif.missionId}`);
      }
      if (notif.candidatureId) {
        if (notif.cible === 'entreprise')
          navigate(`/Entreprise/DetailsCandidature/${notif.candidatureId}`);
        if (notif.cible === 'recruteur')
          navigate(`/Recruteur/DetailsCandidature/${notif.candidatureId}`);
      }
      if (notif.type === 'talent') {
        navigate(`/recruteur/talent-detail/${notif.candidatureId}`);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour :', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_BASE}/api/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      fetchNotifications();
      handleClose();
    } catch (err) {
      console.error('Erreur tout marquer comme lu:', err);
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        classes={{ paper: 'notification-menu' }}
      >
        <Box className="notification-header">
          <Typography variant="h6">Notifications</Typography>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <MenuItem disabled className="notification-empty">
            Aucune notification
          </MenuItem>
        ) : (
          <>
            <MenuItem onClick={markAllAsRead} className="notification-mark-all">
              ✅ Tout marquer comme lu
            </MenuItem>
            <Divider />

            {notifications.slice(0, 5).map((notif) => (
              <React.Fragment key={notif._id}>
                <MenuItem
                  onClick={() => markAsReadAndRedirect(notif)}
                  className={`notification-item ${!notif.isRead ? 'notification-unread' : ''}`}
                >
                  <ListItemText
                    primary={notif.message}
                    secondary={
                      notif.createdAt
                        ? new Date(notif.createdAt).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                        : 'Date inconnue'
                    }
                  />
                </MenuItem>
                <Divider />
              </React.Fragment>
            ))}

            <Box className="notification-footer">
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  handleClose();
                  navigate('/mes-notifications');
                }}
              >
                📋 Voir toutes les notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
